import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { ordersAPI } from '../../services/api';
import { 
  TruckIcon, 
  ClockIcon, 
  CheckCircleIcon,
  PlusCircleIcon,
  MapIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  ExclamationCircleIcon,
  CreditCardIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

// Animated Counter Component
const AnimatedCounter: React.FC<{ value: number; duration?: number; prefix?: string }> = ({ 
  value, 
  duration = 1000,
  prefix = ''
}) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(0);
  const startTimeRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion || value === 0) {
      setCount(value);
      return;
    }

    const animate = (timestamp: number) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const progress = timestamp - startTimeRef.current;
      const percentage = Math.min(progress / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - percentage, 4);
      const currentCount = Math.floor(easeOutQuart * value);
      
      setCount(currentCount);
      countRef.current = currentCount;

      if (percentage < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        setCount(value);
      }
    };

    startTimeRef.current = null;
    animationFrameRef.current = requestAnimationFrame(animate);

    // Cleanup function
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [value, duration]);

  return <>{prefix}{count}</>;
};

const CustomerDashboard: React.FC = () => {
  const { user } = useAuth();
  const { formatPrice } = useCurrency();
  const [stats, setStats] = useState({
    totalOrders: 0,
    activeOrders: 0,
    completedOrders: 0,
    pendingOrders: 0,
    totalSpent: 0
  });
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [pendingPaymentOrders, setPendingPaymentOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [fadeIn, setFadeIn] = useState(false);
  const [regeneratingPayment, setRegeneratingPayment] = useState<number | null>(null);

  const fetchDashboardData = async () => {
    try {
      const ordersData = await ordersAPI.getUserOrders();

      const orders = ordersData || [];
      const activeOrders = orders.filter((o: any) => 
        ['pending', 'accepted', 'assigned', 'picked_up', 'in_transit'].includes(o.status)
      );
      const completedOrders = orders.filter((o: any) => o.status === 'delivered');
      const pendingOrders = orders.filter((o: any) => o.status === 'pending');
      const awaitingPayment = orders.filter((o: any) => 
        o.status === 'pending_payment' || o.status === 'awaiting_payment'
      );
      const totalSpent = orders.reduce((sum: number, o: any) => sum + (o.final_price || 0), 0);

      setStats({
        totalOrders: orders.length,
        activeOrders: activeOrders.length,
        completedOrders: completedOrders.length,
        pendingOrders: pendingOrders.length,
        totalSpent
      });

      setPendingPaymentOrders(awaitingPayment);
      setRecentOrders(orders.filter((o: any) => o.status !== 'pending_payment' && o.status !== 'awaiting_payment').slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (!loading) {
      setTimeout(() => setFadeIn(true), 50);
    }
  }, [loading]);

  const handleResumePayment = async (orderId: number) => {
    setRegeneratingPayment(orderId);
    try {
      const result = await ordersAPI.resumePayment(orderId);
      if (result.payment_url) {
        window.open(result.payment_url, '_blank');
        await fetchDashboardData();
      }
    } catch (error: any) {
      console.error('Failed to regenerate payment link:', error);
      alert(error.response?.data?.detail || 'Failed to regenerate payment link. Please try again.');
    } finally {
      setRegeneratingPayment(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'accepted':
      case 'assigned':
      case 'picked_up':
      case 'in_transit':
        return <TruckIcon className="h-5 w-5 text-volexx-blue-500" />;
      case 'delivered':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get current month for stats display
  const currentMonth = new Date().toLocaleString('default', { month: 'long' });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Skeleton for Welcome Header */}
          <div className="bg-white rounded-volexx-lg shadow-volexx-md border border-gray-100 p-6 sm:p-8 animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-2/3 mb-3"></div>
            <div className="h-5 bg-gray-200 rounded w-1/2"></div>
          </div>

          {/* Skeleton for Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-volexx-lg shadow-volexx-md border border-gray-100 p-6 animate-pulse">
                <div className="flex items-center">
                  <div className="p-3 rounded-volexx bg-gray-200 w-12 h-12 flex-shrink-0"></div>
                  <div className="ml-4 flex-1">
                    <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                    <div className="h-6 bg-gray-200 rounded w-12"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Skeleton for Action Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-gray-200 rounded-volexx-lg shadow-volexx-md p-6 h-40 animate-pulse"></div>
            ))}
          </div>

          {/* Skeleton for Recent Orders */}
          <div className="bg-white rounded-volexx-lg shadow-volexx-md border border-gray-100 animate-pulse">
            <div className="px-6 py-5 border-b border-gray-200">
              <div className="h-6 bg-gray-200 rounded w-32"></div>
            </div>
            <div className="divide-y divide-gray-200">
              {[1, 2, 3].map((i) => (
                <div key={i} className="px-6 py-4">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Welcome Header */}
        <div className={`bg-white rounded-volexx-lg shadow-volexx-md border border-gray-100 p-6 sm:p-8 transition-all duration-700 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 text-gray-900">
            Welcome back, {user?.customer_type === 'corporate' && user?.company_name ? user.company_name : user?.name}! 
          </h1>
          <p className="text-base sm:text-lg text-gray-600">
            {stats.completedOrders > 0 ? (
              <>You have <AnimatedCounter value={stats.completedOrders} /> completed {stats.completedOrders === 1 ? 'order' : 'orders'}. We appreciate your patronage.</>
            ) : (
              <>Ready to schedule your first delivery? Create an order to get started!</>
            )}
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <div className={`group bg-white rounded-volexx-lg shadow-volexx-md border border-gray-100 p-6 transition-all duration-700 hover:shadow-volexx-xl hover:scale-105 hover:-translate-y-1 cursor-pointer ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '100ms' }}>
            <div className="flex items-center">
              <div className="p-3 rounded-volexx bg-volexx-blue-100 flex-shrink-0 transition-all duration-300 group-hover:bg-volexx-blue-200">
                <TruckIcon className="h-6 w-6 text-volexx-blue-600 transition-transform duration-300 group-hover:scale-110" />
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-sm font-semibold text-gray-700">Total Orders</h3>
                <p className="text-2xl font-bold text-volexx-blue-600">
                  <AnimatedCounter value={stats.totalOrders} />
                </p>
              </div>
            </div>
          </div>

          <div className={`group bg-white rounded-volexx-lg shadow-volexx-md border border-gray-100 p-6 transition-all duration-700 hover:shadow-volexx-xl hover:scale-105 hover:-translate-y-1 cursor-pointer ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'} ${stats.activeOrders > 0 ? 'relative' : ''}`} style={{ transitionDelay: '200ms' }}>
            {stats.activeOrders > 0 && (
              <div className="absolute -top-1 -right-1 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-500"></span>
              </div>
            )}
            <div className="flex items-center">
              <div className="p-3 rounded-volexx bg-yellow-100 flex-shrink-0 transition-all duration-300 group-hover:bg-yellow-200">
                <ClockIcon className="h-6 w-6 text-yellow-600 transition-transform duration-300 group-hover:scale-110" />
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-sm font-semibold text-gray-700">Active Orders</h3>
                <p className="text-2xl font-bold text-yellow-600">
                  <AnimatedCounter value={stats.activeOrders} />
                </p>
              </div>
            </div>
          </div>

          <div className={`group bg-white rounded-volexx-lg shadow-volexx-md border border-gray-100 p-6 transition-all duration-700 hover:shadow-volexx-xl hover:scale-105 hover:-translate-y-1 cursor-pointer ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '300ms' }}>
            <div className="flex items-center">
              <div className="p-3 rounded-volexx bg-green-100 flex-shrink-0 transition-all duration-300 group-hover:bg-green-200">
                <CheckCircleIcon className="h-6 w-6 text-green-600 transition-transform duration-300 group-hover:scale-110" />
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-sm font-semibold text-gray-700">Completed</h3>
                <p className="text-2xl font-bold text-green-600">
                  <AnimatedCounter value={stats.completedOrders} />
                </p>
              </div>
            </div>
          </div>

          <div className={`group bg-white rounded-volexx-lg shadow-volexx-md border border-gray-100 p-6 transition-all duration-700 hover:shadow-volexx-xl hover:scale-105 hover:-translate-y-1 cursor-pointer ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '400ms' }}>
            <div className="flex items-center">
              <div className="p-3 rounded-volexx bg-purple-100 flex-shrink-0 transition-all duration-300 group-hover:bg-purple-200">
                <ChartBarIcon className="h-6 w-6 text-purple-600 transition-transform duration-300 group-hover:scale-110" />
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-sm font-semibold text-gray-700">Total Spent</h3>
                <p className="text-2xl font-bold text-purple-600">
                  {formatPrice(stats.totalSpent)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Awaiting Payment Section - Prominent Alert for Incomplete Orders */}
        {pendingPaymentOrders.length > 0 && (
          <div className={`bg-gradient-to-r from-orange-500 to-orange-600 rounded-volexx-lg shadow-volexx-xl border-2 border-orange-300 p-6 transition-all duration-700 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '450ms' }}>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-white/20 rounded-full mr-3">
                <ExclamationCircleIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Complete Your Payment</h2>
                <p className="text-orange-100 text-sm">You have {pendingPaymentOrders.length} order{pendingPaymentOrders.length > 1 ? 's' : ''} awaiting payment</p>
              </div>
            </div>
            <div className="space-y-3">
              {pendingPaymentOrders.slice(0, 3).map((order) => (
                <div key={order.id} className="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold">Order #{order.id?.toString().slice(-8) || 'N/A'}</p>
                    <p className="text-orange-100 text-sm truncate">
                      {order.stops && order.stops.length > 0 ? (
                        <>{order.stops[0]?.address?.substring(0, 30)}...</>
                      ) : 'Delivery order'}
                    </p>
                    <p className="text-white font-bold mt-1">{formatPrice(order.final_price || 0)}</p>
                  </div>
                  <div className="ml-4 flex-shrink-0 flex flex-col sm:flex-row gap-2">
                    {order.payment_url ? (
                      <>
                        <a
                          href={order.payment_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-white text-orange-600 font-semibold rounded-lg hover:bg-orange-50 transition-all duration-200 shadow-md hover:shadow-lg"
                        >
                          <CreditCardIcon className="h-5 w-5 mr-2" />
                          Pay Now
                        </a>
                        <button
                          onClick={() => handleResumePayment(order.id)}
                          disabled={regeneratingPayment === order.id}
                          className="inline-flex items-center px-3 py-2 bg-white/20 text-white text-sm font-medium rounded-lg hover:bg-white/30 transition-all duration-200 disabled:opacity-50"
                        >
                          {regeneratingPayment === order.id ? 'Loading...' : 'New Link'}
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleResumePayment(order.id)}
                        disabled={regeneratingPayment === order.id}
                        className="inline-flex items-center px-4 py-2 bg-white text-orange-600 font-semibold rounded-lg hover:bg-orange-50 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50"
                      >
                        <CreditCardIcon className="h-5 w-5 mr-2" />
                        {regeneratingPayment === order.id ? 'Loading...' : 'Get Payment Link'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {pendingPaymentOrders.length > 3 && (
                <Link
                  to="/orders"
                  className="block text-center text-white/90 hover:text-white text-sm font-medium py-2"
                >
                  View all {pendingPaymentOrders.length} pending orders →
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Quick Action Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <Link
            to="/create-order"
            className={`bg-gradient-orange rounded-volexx-lg shadow-volexx-md p-6 text-center group hover:shadow-volexx-xl transform hover:scale-105 transition-all duration-300 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: '500ms' }}
          >
            <PlusCircleIcon className="h-12 w-12 text-white mx-auto mb-3 transition-transform duration-300 group-hover:rotate-90" />
            <h3 className="text-xl font-bold mb-2 text-white">Create New Order</h3>
            <p className="text-sm text-white opacity-90">Schedule a new delivery</p>
          </Link>

          <Link
            to="/luxury-hire"
            className={`bg-gradient-to-r from-slate-800 to-slate-900 rounded-volexx-lg shadow-volexx-md p-6 text-center group hover:shadow-volexx-xl transform hover:scale-105 transition-all duration-300 relative overflow-hidden ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: '550ms' }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 via-transparent to-amber-600/10"></div>
            <div className="absolute top-2 right-2">
              <span className="px-2 py-0.5 bg-amber-500 text-slate-900 text-xs font-bold rounded-full">PREMIUM</span>
            </div>
            <SparklesIcon className="h-12 w-12 text-amber-400 mx-auto mb-3 transition-transform duration-300 group-hover:scale-110 relative z-10" />
            <h3 className="text-xl font-bold mb-2 text-white relative z-10">Luxury Vehicle Hire</h3>
            <p className="text-sm text-amber-200 opacity-90 relative z-10">Premium cars & chauffeurs</p>
          </Link>

          <Link
            to="/track-orders"
            className={`bg-gradient-blue rounded-volexx-lg shadow-volexx-md p-6 text-center group hover:shadow-volexx-xl transform hover:scale-105 transition-all duration-300 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: '600ms' }}
          >
            <MapIcon className="h-12 w-12 text-white mx-auto mb-3 transition-transform duration-300 group-hover:scale-110" />
            <h3 className="text-xl font-bold mb-2 text-white">Track Orders</h3>
            <p className="text-sm text-white opacity-90">View real-time locations</p>
          </Link>

          <Link
            to="/wallet"
            className={`bg-gradient-to-r from-green-500 to-green-600 rounded-volexx-lg shadow-volexx-md p-6 text-center group hover:shadow-volexx-xl transform hover:scale-105 transition-all duration-300 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: '700ms' }}
          >
            <CurrencyDollarIcon className="h-12 w-12 text-white mx-auto mb-3 transition-transform duration-300 group-hover:scale-110" />
            <h3 className="text-xl font-bold mb-2 text-white">Wallet</h3>
            <p className="text-sm text-white opacity-90">Manage your wallet & add funds</p>
          </Link>
        </div>

        {/* Monthly Stats Banner */}
        <div className={`bg-gradient-to-r from-volexx-blue-500 to-volexx-blue-600 rounded-volexx-lg shadow-volexx-md p-6 text-white transition-all duration-700 hover:shadow-volexx-xl hover:scale-[1.02] ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '800ms' }}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold mb-2">Your {currentMonth} Summary</h3>
              <p className="text-2xl font-bold"><AnimatedCounter value={stats.completedOrders} /> deliveries completed</p>
              <p className="text-sm opacity-90 mt-1">Total spent: {formatPrice(stats.totalSpent)}</p>
            </div>
            <ChartBarIcon className="h-16 w-16 opacity-20" />
          </div>
        </div>

        {/* Recent Orders */}
        <div className={`bg-white rounded-volexx-lg shadow-volexx-md border border-gray-100 transition-all duration-700 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '900ms' }}>
          <div className="px-6 py-5 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Recent Orders</h2>
              <Link
                to="/orders"
                className="text-sm font-semibold text-volexx-blue-500 hover:text-volexx-blue-600 transition-all duration-200 relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-volexx-blue-600 after:transition-all after:duration-300 hover:after:w-full"
              >
                View All →
              </Link>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {recentOrders.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-semibold text-gray-900">No orders yet</h3>
                <p className="mt-1 text-sm text-gray-500">Get started by creating your first order.</p>
                <div className="mt-6">
                  <Link to="/create-order" className="btn-primary">
                    Create Order
                  </Link>
                </div>
              </div>
            ) : (
              recentOrders.map((order) => (
                <Link
                  key={order.id}
                  to={`/orders/${order.id}`}
                  className="px-6 py-4 hover:bg-gray-50 transition-all duration-300 block hover:translate-x-2"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="flex-shrink-0">
                        {getStatusIcon(order.status)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          Order #{order.id?.toString().slice(-8) || 'N/A'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {order.stops && order.stops.length > 0 ? (
                            <>
                              {order.stops[0]?.address} → {order.stops[order.stops.length - 1]?.address}
                            </>
                          ) : 'No route data'}
                        </p>
                      </div>
                    </div>
                    <div className="ml-4 flex items-center space-x-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                        order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-volexx-blue-100 text-volexx-blue-800'
                      }`}>
                        {getStatusText(order.status)}
                      </span>
                      <span className="text-sm font-bold text-gray-900">
                        {formatPrice(order.final_price || 0)}
                      </span>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;
